import { type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { Plus, RefreshCw, Clock } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import { cn } from '@/lib/utils'
import { useQuery } from '@tanstack/react-query'
import { accountQueries } from '@/services/accountApi'
import { autoTopupQueries } from '@/services/autoTopupApi'
import { useCurrency } from '@/hooks/useCurrency'

interface BalanceHeroCardProps {
  onTopUp: () => void
  badge?: ReactNode
  secondaryCta?: ReactNode
  compact?: boolean
}

export function BalanceHeroCard({
  onTopUp,
  badge,
  secondaryCta
}: BalanceHeroCardProps) {
  const { t } = useTranslation(['app', 'common'])
  const { data: balanceData, isLoading, error, refetch } = useQuery(accountQueries.balance())
  const { data: autoTopupConfig } = useQuery(autoTopupQueries.config())
  const { formatCurrency } = useCurrency()

  const balanceAmount = balanceData?.accountBalance?.amount
  const formattedBalance = balanceAmount != null
    ? formatCurrency(balanceAmount / 100, { useIntl: true })
    : null

  const pendingAmountCents = balanceData?.accountBalance?.pendingAmountCents ?? 0
  const hasPending = pendingAmountCents > 0

  return (
    <div className={cn(
      'relative overflow-hidden rounded-2xl bg-gradient-to-br from-primary to-primary/80 text-primary-foreground shadow-xl p-6',
    )}>
      <div className="relative">
        {isLoading ? (
          <div className={cn('space-y-3')}>
            <Skeleton className={cn('bg-primary-foreground/20', 'h-3 w-32')} />
            <Skeleton className={cn('bg-primary-foreground/20', 'h-10 w-48')} />
            <Skeleton className="h-px w-full bg-primary-foreground/10" />
            <div className={cn('flex gap-3 pt-1')}>
              <Skeleton className={cn('rounded-full bg-primary-foreground/20 h-10 w-28')} />
              <Skeleton className={cn('rounded-full bg-primary-foreground/20 h-10 w-32')} />
            </div>
          </div>
        ) : error ? (
          <div className={cn('flex flex-col items-center justify-center', 'gap-3 py-6')}>
            <p className="text-sm text-primary-foreground/80">{t('account.balanceError')}</p>
            <Button variant="secondary" size={'sm'} onClick={() => refetch()}>
              {t('common.retry')}
            </Button>
          </div>
        ) : (
          <>
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-primary-foreground/60">
                  {t('account.currentBalance')}
                </p>
                <div className={cn('flex items-baseline gap-2 mt-1' )}>
                  <span className={cn('font-black tracking-tight text-4xl')}>
                    {formattedBalance ?? '—'}
                  </span>
                </div>
                <p className={cn('text-sm mt-1 flex items-center gap-1', hasPending ? 'text-primary-foreground/70' : 'text-primary-foreground/60')}>
                  {hasPending && <Clock className="h-3 w-3 shrink-0" />}
                  {t('balance.pendingTopUp', {
                    amount: formatCurrency(pendingAmountCents / 100, { useIntl: true }),
                  })}
                </p>
              </div>
              <div className="shrink-0 flex flex-col gap-2">
                {badge}
                {autoTopupConfig?.enabled && (
                  <Badge variant="secondary" className="gap-1 bg-white/20 text-white border-0">
                    <RefreshCw className="h-3 w-3" />
                    {t('balance.autoTopupEnabled')}
                  </Badge>
                )}
              </div>
            </div>

            <div className={cn('h-px bg-primary-foreground/10 mt-5')} />

            <div className={cn('flex flex-wrap items-center gap-3 mt-4')}>
              <Button
                size={'default'}
                variant="secondary"
                shape="pill"
                className="font-bold shadow-md"
                onClick={onTopUp}
              >
                <Plus className="mr-2 h-4 w-4" />
                {t('account.topUp')}
              </Button>
              {secondaryCta}
            </div>
          </>
        )}
      </div>
    </div>
  )
}

